<template>
  <div>
    <div v-if="!mappings.length" class="empty-mapping">
      <i class="bx bx-transfer" style="font-size: 32px; color: #b8bdd0;"></i>
      <p>Nenhum campo mapeado.</p>
    </div>

    <table v-else class="mapping-table">
      <thead>
        <tr>
          <th style="width: 32px;"></th>
          <th>Campo de Origem</th>
          <th>{{ fixedTarget ? 'Campo no Destino' : 'Coluna na Planilha' }}</th>
          <th style="width: 36px;"></th>
        </tr>
      </thead>
      <draggable
        tag="tbody"
        v-model="mappings"
        handle=".mapping-drag"
        ghost-class="mapping-row-ghost"
        animation="150"
        @end="emitUpdate"
      >
        <tr v-for="(row, index) in mappings" :key="row._uid">
          <td>
            <span class="mapping-drag" title="Arraste para reordenar">
              <i class="bx bx-grid-vertical" style="font-size: 16px; color: #b8bdd0;"></i>
            </span>
          </td>
          <td>
            <select
              class="form-select"
              v-model="row.selectedField"
              @change="onFieldSelect(index, row.selectedField)"
            >
              <option value="">-- Selecione --</option>
              <optgroup
                v-for="cat in availableFields"
                :key="cat.category"
                :label="cat.label"
              >
                <option
                  v-for="field in cat.fields"
                  :key="field.source_type + ':' + field.source_key"
                  :value="field.source_type + ':' + field.source_key"
                >
                  {{ field.label }}
                </option>
              </optgroup>
            </select>
          </td>
          <td>
            <!-- Destino fixo (select dos campos do serviço) -->
            <select
              v-if="fixedTarget"
              class="form-select"
              v-model="row.target_column"
              @change="emitUpdate"
            >
              <option value="">-- Selecione --</option>
              <option
                v-for="t in (targetFields || [])"
                :key="t.key"
                :value="t.key"
              >
                {{ t.label }}
              </option>
            </select>
            <!-- Destino livre (nome da coluna na planilha) -->
            <input
              v-else
              type="text"
              class="form-input"
              v-model="row.target_column"
              placeholder="Ex: NOME"
              @input="emitUpdate"
            />
          </td>
          <td>
            <button class="mapping-remove" @click="removeRow(index)" title="Remover">
              <i class="bx bx-x" style="font-size: 18px;"></i>
            </button>
          </td>
        </tr>
      </draggable>
    </table>

    <button class="add-mapping-btn" @click="addRow">
      <i class="bx bx-plus-circle" style="font-size: 15px;"></i>
      Adicionar campo
    </button>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'FieldMappingTable',
  components: { draggable },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    availableFields: {
      type: Array,
      default: () => []
    },
    // Lista fixa de campos de destino [{key, label}] — quando presente, o
    // destino vira um <select> em vez de texto livre.
    targetFields: {
      type: Array,
      default: null
    },
    fixedTarget: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      mappings: [],
      uidSeq: 0,
      // Evita reconstruir a lista quando o valor volta como "eco" da nossa
      // própria emissão (senão as chaves _uid mudam e o drag fica instável).
      internalUpdate: false
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        if (this.internalUpdate) {
          this.internalUpdate = false
          return
        }
        if (val && val.length) {
          this.mappings = val.map(m => ({
            ...m,
            selectedField: m.source_type && m.source_key ? `${m.source_type}:${m.source_key}` : '',
            _uid: this.nextUid()
          }))
        } else {
          this.mappings = []
        }
      }
    }
  },
  methods: {
    nextUid () {
      this.uidSeq += 1
      return 'fm_' + this.uidSeq
    },
    findField (sourceType, sourceKey) {
      for (const cat of this.availableFields) {
        const found = cat.fields.find(
          f => f.source_type === sourceType && f.source_key === sourceKey
        )
        if (found) return found
      }
      return null
    },
    addRow () {
      this.mappings.push({
        source_type: '',
        source_key: '',
        target_column: '',
        selectedField: '',
        sequence: this.mappings.length,
        _uid: this.nextUid()
      })
    },
    removeRow (index) {
      this.mappings.splice(index, 1)
      this.emitUpdate()
    },
    onFieldSelect (index, val) {
      if (val) {
        const [sourceType, ...rest] = val.split(':')
        const sourceKey = rest.join(':')
        this.mappings[index].source_type = sourceType
        this.mappings[index].source_key = sourceKey

        if (!this.mappings[index].target_column) {
          if (this.fixedTarget) {
            // Destino fixo: tenta casar automaticamente pela chave de origem
            // (ex: source 'email' \u2192 target 'email'). Sen\u00e3o, deixa o usu\u00e1rio escolher.
            const match = (this.targetFields || []).find(t => t.key === sourceKey)
            if (match) {
              this.mappings[index].target_column = match.key
            }
          } else {
            const field = this.findField(sourceType, sourceKey)
            if (field) {
              const base = field.label
                .toUpperCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/[^A-Z0-9]+/g, '_')
                .replace(/^_|_$/g, '')
                .substring(0, 30)
              // Nome de coluna \u00daNICO: dois campos podem ter o mesmo r\u00f3tulo (ex.:
              // "Nome" do participante e "Nome" do controle de inscri\u00e7\u00e3o).
              this.mappings[index].target_column = this.uniqueTarget(base, sourceType, index)
            }
          }
        }
      } else {
        this.mappings[index].source_type = ''
        this.mappings[index].source_key = ''
      }
      this.emitUpdate()
    },
    // Garante um target_column \u00fanico na lista. Se colidir, tenta um sufixo por
    // origem ("(CONTROLE)", "(FORM)", "(COLETA)") e, se ainda colidir, numera.
    uniqueTarget (base, sourceType, exceptIndex) {
      const taken = new Set(
        this.mappings
          .filter((m, i) => i !== exceptIndex)
          .map(m => (m.target_column || '').toUpperCase())
      )
      if (!taken.has(base.toUpperCase())) return base

      const suffixes = {
        registration_control: ' (CONTROLE)',
        form_field: ' (FORM)',
        card_field: ' (FORM)',
        lead_field: ' (COLETA)',
        lead_meta: ' (COLETA)'
      }
      let candidate = (base + (suffixes[sourceType] || '')).substring(0, 30)
      let n = 2
      while (taken.has(candidate.toUpperCase())) {
        candidate = (base + ' ' + n).substring(0, 30)
        n++
      }
      return candidate
    },
    emitUpdate () {
      const output = this.mappings.map((m, i) => ({
        source_type: m.source_type,
        source_key: m.source_key,
        target_column: m.target_column,
        sequence: i
      }))
      this.internalUpdate = true
      this.$emit('input', output)
    }
  }
}
</script>

<style scoped>
.mapping-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #e2e5ed;
  border-radius: 6px;
  overflow: hidden;
}
.mapping-table th {
  background: #f8f9fb;
  padding: 9px 14px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #8d92a9;
  text-align: left;
  border-bottom: 1px solid #e2e5ed;
}
.mapping-table td {
  padding: 6px 14px;
  border-bottom: 1px solid #edf0f5;
  vertical-align: middle;
}
.mapping-table tr:last-child td { border-bottom: none; }

.mapping-table .form-select,
.mapping-table .form-input {
  width: 100%;
  padding: 7px 10px;
  font-size: 12px;
  border: 1px solid #e2e5ed;
  border-radius: 6px;
  font-family: inherit;
  color: #1e2235;
  background: #fff;
  transition: all 0.15s;
  outline: none;
}
.mapping-table .form-select:focus,
.mapping-table .form-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px #eff4ff;
}
.mapping-table .form-input::placeholder { color: #b8bdd0; }
.mapping-table .form-select optgroup { font-weight: 600; color: #1e2235; }
.mapping-table .form-select option { font-weight: 400; color: #4d5269; }

.mapping-drag {
  color: #b8bdd0;
  cursor: grab;
  display: flex;
  align-items: center;
}
.mapping-drag:active { cursor: grabbing; }

/* Placeholder enquanto arrasta a linha */
.mapping-row-ghost td {
  opacity: 0.5;
  background: #eff4ff;
}
.mapping-remove {
  color: #b8bdd0;
  cursor: pointer;
  background: none;
  border: none;
  padding: 4px;
  transition: color 0.15s;
  display: flex;
}
.mapping-remove:hover { color: #f46a6a; }

.add-mapping-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  font-size: 12px;
  font-weight: 500;
  color: #2563eb;
  cursor: pointer;
  background: none;
  border: none;
  font-family: inherit;
}
.add-mapping-btn:hover { opacity: 0.7; }

.empty-mapping {
  text-align: center;
  padding: 28px 0;
  color: #8d92a9;
}
.empty-mapping p {
  margin-top: 8px;
  font-size: 13px;
}
</style>
